import { Button, Form, Input, Modal, notification } from 'antd';
import dayjs from 'dayjs';
import _, { find } from 'lodash';
import { useEffect, useState } from 'react';
import { IoArrowUndoOutline } from 'react-icons/io5';
import { useParams } from 'react-router-dom';
import { PriorityOptions, TASk_TABS } from '../../constants';
import { formatAttachments } from '../../helper';
import useRestaurants from '../../hooks/useRestaurants';
import { sendTaskCompletedEmail, sendUndoTaskCompletionEmail } from '../../redux/actions/email';
import { getTasks, undoTaskCompletion, updateTaskProgress } from '../../redux/actions/tasks';
import { getUploadUrl, uploadFileToS3 } from '../../redux/actions/upload';
import { getFileUrl } from '../../utils';
import UploadAttachment from '../UploadAttachment';

const CompleteTaskModal = ({ open, onClose, task }) => {
  const { id } = useParams();
  const { restaurants, users } = useRestaurants()
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);

  const { fileNames, list } = formatAttachments(fileList);

  const managers = _.chain(users)?.filter({ role: 2 })?.value();
  const restaurant = restaurants?.find((rest) => Number(rest.id) === Number(id));

  const handleUndoTask = async () => {
    const res = await undoTaskCompletion({
      taskId: task.id,
      restaurant_id: id,
      filter: TASk_TABS.PENDING,
    });
    if (res.ack) {
      sendUndoTaskCompletionEmail({
        managers,
        task: {
          ...task,
          due_date: task?.due_date ? dayjs(task?.due_date).format("'DD/MM/YYYY HH:mm") : "-",
          restaurant: restaurant?.title,
          priority: find(PriorityOptions, { value: task?.priority })?.label,
        },
      });
    }
    notification.destroy();
  }

  const handleFinish = async (values) => {
    const body = {
      completion_files: fileNames?.length ? JSON.stringify(fileNames) : null,
      completion_notes: values?.notes,
      progress: 1,
    };
    const res = await updateTaskProgress(task?.id, body, { toast: false });
    const isFilesMatch = _.isEqual(fileNames, task && JSON.parse(task?.completion_files));

    if (res && !isFilesMatch) {
      const uploadPromises = [];
      list.forEach((f) => {
        uploadPromises.push(
          getUploadUrl(f, 'tasks', res?.record_id ?? task.id)
            .then((uploadUrl) => uploadFileToS3(uploadUrl, f.file, f.file.type))
            .catch((error) => console.error('Error uploading file:', error))
        );
      });
    }

    if (res?.ack) {
      sendTaskCompletedEmail({
        managers,
        task: {
          ...task,
          due_date: task?.due_date ? dayjs(task?.due_date).format("'DD/MM/YYYY HH:mm") : "-",
          restaurant: restaurant?.title,
          priority: find(PriorityOptions, { value: task?.priority })?.label,
          completion_date: dayjs(res?.task?.completed_at).format("'DD/MM/YYYY HH:mm"),
        },
      });

      notification.open({
        message: `${task?.title} is completed`,
        description: 'You can undo the completion of this task.',
        duration: 10,
        placement: 'bottomRight',
        showProgress: true,
        pauseOnHover: false,
        btn: (
          <Button
            variant="dashed"
            color="primary"
            icon={<IoArrowUndoOutline />}
            ghost
            size='small'
            styles={{
              icon: {
                fontSize: 16,
                display: 'flex',
                alignItems: 'center',
              },
            }}
            onClick={handleUndoTask}
          >
            Undo Completion
          </Button>
        ),
      });
      getTasks({ id, filter: TASk_TABS.PENDING });
      onClose();
    }
  };

  useEffect(() => {
    if (task.completion_files) {
      const files = JSON.parse(task?.completion_files);
      const formatFiles = files.map((file) => {
        return {
          uid: file,
          name: file,
          status: 'done',
          url: getFileUrl('tasks', task?.id, file),
          payload: true,
        };
      });
      setFileList(formatFiles);
    }
  }, [task])

  return (
    <Modal
      title="Complete Task"
      open={open}
      centered
      onOk={() => form.submit()}
      onCancel={onClose}
      okText="Mark as Complete"
      styles={{
        mask: {
          backdropFilter: 'blur(4px)',
          backgroundColor: '#00000015',
        },
        content: {
          borderRadius: '12px',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      <Form form={form} onFinish={handleFinish} layout="vertical">
        <Form.Item
          label="Notes"
          name="notes"
          rules={[{ required: true, message: 'Notes is required!.' }]}
        >
          <Input.TextArea rows={4} placeholder="Add a notes..." />
        </Form.Item>

        <Form.Item
          label="Attachment"
          name="attachment"
        >
          <UploadAttachment fileList={fileList} onChange={(file) => setFileList(file)} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CompleteTaskModal;
